import { ReactNode, useState, useEffect } from "react";
import { cn } from "@/lib/utils";

export type ChemSection = { id: string; label: string; icon?: any };

export const ChemPageShell = ({
  title, subtitle, sections, children,
}: { title: string; subtitle: string; sections: ChemSection[]; children: ReactNode }) => {
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [sections]);

  return (
    <div>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-teal/70 mb-1">Operations · Chemical PSV Fleet</p>
        <h1 className="text-3xl font-bold">{title}</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">{subtitle}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <aside className="hidden lg:block">
          <nav className="sticky top-20 space-y-0.5 max-h-[calc(100vh-6rem)] overflow-y-auto pr-2">
            {sections.map((s, i) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={() => setActive(s.id)}
                className={cn(
                  "block px-3 py-1.5 rounded-md text-[12px] font-body transition-colors",
                  active === s.id
                    ? "bg-teal/10 text-teal border-l-2 border-teal"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <span className="text-[10px] text-muted-foreground/60 mr-2">{String(i+1).padStart(2,"0")}</span>
                {s.label}
              </a>
            ))}
          </nav>
        </aside>
        <div className="space-y-10 min-w-0">{children}</div>
      </div>
    </div>
  );
};

export const ChemSectionBlock = ({ id, n, title, blurb, children }: { id: string; n: number; title: string; blurb?: string; children: ReactNode }) => (
  <section id={id} className="scroll-mt-24">
    <div className="border-b border-border pb-3 mb-4">
      <p className="text-[10px] font-bold tracking-[2px] uppercase text-teal">Section {String(n).padStart(2,"0")}</p>
      <h2 className="text-xl font-bold mt-1">{title}</h2>
      {blurb && <p className="text-xs text-muted-foreground mt-1">{blurb}</p>}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);
